import React from 'react';
import { OnChangeValue } from 'react-select';
import AccountTreeOutlined from '@mui/icons-material/AccountTreeOutlined';
import { Checkbox } from 'src/components/atoms';
import {
  StyledIcon,
  StyledIconInButton,
} from 'src/components/foundations/foundations.styles';
import { DefaultOption, FormInput } from 'src/components/molecules';
import { FormInputVariant, FormState } from 'src/types';
import { useTheme } from 'styled-components';
import {
  getTrimmedLength,
  MAX_DATA_GRID_COLUMNS,
  MAX_OPTIONS,
  MIN_OPTIONS,
  OPTION_TEXT_MAX_LENGTH,
} from '../../helpers/validation';
import { DATA_GRID_COLUMN_TYPE_OPTIONS, QuestionOption } from '../../types';
import {
  AddFirstOptionButton,
  AddOptionButton,
  AnswerOptionsContainer,
  AnswerOptionsLabel,
  CharacterCounter,
  DataGridFlagIfAnsweredRow,
  DeleteIconButton,
  EmptyStateContainer,
  EmptyStateText,
  FieldInputWrapper,
  FlowIconButton,
  MaxOptionsAlert,
  MaxOptionsAlertMessage,
  OptionBlock,
  OptionDeleteControl,
  OptionDependencyGroup,
  OptionDropdownField,
  OptionErrorContent,
  OptionErrorRow,
  OptionHelperText,
  OptionInputField,
  OptionMetaRow,
  OptionsList as OptionsListContainer,
  RequiredMark,
  StyledErrorMessage,
} from './OptionsList.styles';

interface OptionsConfig {
  title: string;
  addButtonText: string;
  emptyText: string;
  addFirstButtonText: string;
  placeholder: string;
}

interface Props {
  options?: QuestionOption[];
  answerType?: string;
  config: OptionsConfig;
  optionsError?: string | null;
  onAddFirstOption: () => void;
  onAddOption: () => void;
  onOptionTextChange: (optionId: string, text: string) => void;
  onDeleteOption: (optionId: string) => void;
  onToggleOptionFlagged: (optionId: string) => void;
  showOptionFlagged?: boolean;
  onNextActionChange: (
    optionId: string,
    value: OnChangeValue<DefaultOption, false>,
  ) => void;
  hideOptionFlowControls?: boolean;
  showDataGridFlagIfAnswered?: boolean;
  flagIfAnswered?: boolean;
  onFlagIfAnsweredChange?: () => void;
  isLastQuestion?: boolean;
  questionIndex?: number;
  allQuestions?: import('../../types').QuestionData[];
  'data-testid'?: string;
}

const MAX_LABEL_LENGTH = 30;

const truncateLabel = (text: string): string => {
  if (text.length <= MAX_LABEL_LENGTH) {
    return text;
  }
  return `${text.substring(0, MAX_LABEL_LENGTH)}...`;
};

const buildNextActionOptions = (
  questionIndex: number,
  allQuestions: import('../../types').QuestionData[],
): Array<{ label: string; value: string; tooltip?: string }> => {
  const options: Array<{ label: string; value: string; tooltip?: string }> = [
    { label: 'Next question', value: 'next' },
  ];

  for (let i = questionIndex + 2; i < allQuestions.length; i++) {
    const q = allQuestions[i];
    const fullText = q.questionText?.trim() || `Question ${q.displayNumber}`;
    const truncatedLabel = truncateLabel(fullText);
    options.push({
      label: truncatedLabel,
      value: `question:${q.id}`,
      tooltip: fullText.length > MAX_LABEL_LENGTH ? fullText : undefined,
    });
  }

  options.push({ label: 'Submit Form', value: 'submit' });
  return options;
};

const columnTypeOptions = DATA_GRID_COLUMN_TYPE_OPTIONS;

export const OptionsList: React.FC<Props> = ({
  options,
  answerType,
  config,
  optionsError,
  onAddFirstOption,
  onAddOption,
  onOptionTextChange,
  onDeleteOption,
  onToggleOptionFlagged,
  showOptionFlagged = true,
  onNextActionChange,
  hideOptionFlowControls = false,
  showDataGridFlagIfAnswered = false,
  flagIfAnswered = false,
  onFlagIfAnsweredChange,
  isLastQuestion = false,
  questionIndex = 0,
  allQuestions = [],
  ...props
}) => {
  const theme = useTheme();
  const hasOptions = options && options.length > 0;
  const isDataGrid = answerType === 'data_grid';
  const maxOptionsForType = isDataGrid ? MAX_DATA_GRID_COLUMNS : MAX_OPTIONS;
  const isAtMaxOptions = (options?.length || 0) >= maxOptionsForType;
  const regularOptionsCount =
    options?.filter((opt) => !opt.isOtherOption).length || 0;
  const isAtMinOptions = regularOptionsCount <= MIN_OPTIONS;
  const showPerOptionFlagged = showOptionFlagged && !isDataGrid;
  const showDataGridFlag =
    isDataGrid && showDataGridFlagIfAnswered && !!onFlagIfAnsweredChange;
  const hideFlowControls = hideOptionFlowControls && !isDataGrid;
  const showOtherOptionInUi =
    answerType === 'radio' || answerType === 'checkbox';
  const visibleOptions = showOtherOptionInUi
    ? (options ?? [])
    : (options?.filter((option) => !option.isOtherOption) ?? []);

  const jumpTargetKey = allQuestions
    .slice(questionIndex + 2)
    .map((q) => `${q.id}:${q.displayNumber}:${q.questionText ?? ''}`)
    .join('|');

  const nextActionOptions = React.useMemo(() => {
    const options = buildNextActionOptions(questionIndex, allQuestions);

    // If it's the last question, filter out the "Next question" option
    if (isLastQuestion) {
      return options.filter((opt) => opt.value !== 'next');
    }

    return options;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionIndex, allQuestions.length, jumpTargetKey, isLastQuestion]);

  const renderOption = (
    option: QuestionOption,
    index: number,
    isLastVisibleOption: boolean,
  ) => {
    const optionCharCount =
      option.isOtherOption && option.text === 'Other (optional)'
        ? 0
        : getTrimmedLength(option.text);
    const charCounter = (
      <CharacterCounter
        isError={optionCharCount > OPTION_TEXT_MAX_LENGTH}
        data-testid={`${props['data-testid']}-option-char-counter-${index}`}
        aria-live="polite"
      >
        {optionCharCount}/{OPTION_TEXT_MAX_LENGTH}
      </CharacterCounter>
    );

    return (
      <OptionBlock
        key={option.id}
        $hasDivider={!isLastVisibleOption}
        $hasTopSpacing={index > 0}
        $hideFlowControls={hideFlowControls}
        data-testid={`${props['data-testid']}-option-item-${index}`}
      >
        <OptionMetaRow $counterOnly={!showPerOptionFlagged}>
          {showPerOptionFlagged && (
            <Checkbox
              checked={option.isFlagged || false}
              onChange={() => onToggleOptionFlagged(option.id)}
              label="Flagged"
              labelPosition="right"
              data-testid={`${props['data-testid']}-option-conflict-checkbox-${index}`}
            />
          )}
          {!option.isOtherOption && charCounter}
        </OptionMetaRow>
        <OptionInputField>
          <FieldInputWrapper>
            <FormInput
              variant={FormInputVariant.INPUT}
              state={
                option.isOtherOption
                  ? FormState.DISABLED
                  : option.textError
                    ? FormState.ERROR
                    : FormState.DEFAULT
              }
              value={
                option.isOtherOption && option.text === 'Other (optional)'
                  ? ''
                  : option.text
              }
              onChange={(e) => {
                if (!option.isOtherOption) {
                  onOptionTextChange(option.id, e.target.value);
                }
              }}
              placeholder={
                option.isOtherOption ? 'Other (optional)' : config.placeholder
              }
              data-testid={`${props['data-testid']}-option-input-${index}`}
              tabIndex={option.isOtherOption ? -1 : 0}
              $removeMarginBottom
              aria-invalid={!!option.textError}
              aria-describedby={
                option.textError
                  ? `${props['data-testid']}-option-error-${index}`
                  : option.isOtherOption && option.helperText
                    ? `${props['data-testid']}-option-helper-${index}`
                    : undefined
              }
            />
          </FieldInputWrapper>
        </OptionInputField>
        {option.isOtherOption && option.helperText && (
          <OptionErrorRow>
            <OptionHelperText
              id={`${props['data-testid']}-option-helper-${index}`}
              data-testid={`${props['data-testid']}-option-helper-${index}`}
            >
              {option.helperText}
            </OptionHelperText>
          </OptionErrorRow>
        )}
        {option.textError && (
          <OptionErrorRow>
            <OptionErrorContent>
              <StyledIcon
                className="uil uil-exclamation-circle"
                size="16px"
                color={
                  theme.isVaultVariant
                    ? theme.newPalette.red.R300
                    : theme.atlas.semantic.color.status.error.text
                }
                data-testid={`${props['data-testid']}-option-error-icon-${index}`}
                aria-hidden
              />
              <StyledErrorMessage
                id={`${props['data-testid']}-option-error-${index}`}
                role="alert"
                data-testid={`${props['data-testid']}-option-error-${index}`}
              >
                {option.textError}
              </StyledErrorMessage>
            </OptionErrorContent>
          </OptionErrorRow>
        )}
        {!isDataGrid && !hideFlowControls && (
          <OptionDependencyGroup>
            <FlowIconButton>
              <AccountTreeOutlined
                sx={{
                  fontSize: '24px',
                  color: theme.isVaultVariant
                    ? theme.newPalette.neutral.N800
                    : theme.atlas.semantic.color.type.default,
                }}
                data-testid={`${props['data-testid']}-option-branch-icon-${index}`}
              />
            </FlowIconButton>
            <OptionDropdownField>
              <FormInput
                variant={FormInputVariant.DROPDOWN}
                state={FormState.DEFAULT}
                options={nextActionOptions}
                value={
                  isLastQuestion
                    ? nextActionOptions.find(
                        (opt) =>
                          opt.value === option.nextAction &&
                          opt.value !== 'next',
                      ) ||
                      nextActionOptions.find((opt) => opt.value === 'submit')
                    : nextActionOptions.find(
                        (opt) => opt.value === option.nextAction,
                      ) || nextActionOptions[0]
                }
                onChange={(value) =>
                  onNextActionChange(
                    option.id,
                    value as OnChangeValue<DefaultOption, false>,
                  )
                }
                data-testid={`${props['data-testid']}-option-dropdown-${index}`}
                $removeMarginBottom
              />
            </OptionDropdownField>
          </OptionDependencyGroup>
        )}
        {!hideFlowControls && isDataGrid && (
          <OptionDependencyGroup>
            <OptionDropdownField>
              <FormInput
                variant={FormInputVariant.DROPDOWN}
                state={FormState.DEFAULT}
                options={columnTypeOptions}
                value={
                  columnTypeOptions.find(
                    (opt) => opt.value === option.nextAction,
                  ) || columnTypeOptions[0]
                }
                onChange={(value) =>
                  onNextActionChange(
                    option.id,
                    value as OnChangeValue<DefaultOption, false>,
                  )
                }
                data-testid={`${props['data-testid']}-option-dropdown-${index}`}
                $removeMarginBottom
              />
            </OptionDropdownField>
          </OptionDependencyGroup>
        )}
        <OptionDeleteControl $hideFlowControls={hideFlowControls}>
          <DeleteIconButton
            size="small"
            variant="tertiary"
            iconName="uil uil-trash-alt"
            noBorder
            onClick={() => onDeleteOption(option.id)}
            data-testid={`${props['data-testid']}-option-delete-button-${index}`}
            aria-label="Delete option"
            disabled={!option.isOtherOption && isAtMinOptions}
          />
        </OptionDeleteControl>
      </OptionBlock>
    );
  };

  return (
    <AnswerOptionsContainer
      data-testid={`${props['data-testid']}-answer-options`}
    >
      {!hasOptions ? (
        <>
          <AnswerOptionsLabel
            data-testid={`${props['data-testid']}-answer-options-title`}
          >
            {config.title}
            <RequiredMark>*</RequiredMark>
          </AnswerOptionsLabel>
          <EmptyStateContainer
            $hasError={!!optionsError}
            data-testid={`${props['data-testid']}-answer-options-empty-state`}
          >
            <EmptyStateText
              $hasError={!!optionsError}
              data-testid={`${props['data-testid']}-answer-options-empty-text`}
            >
              {optionsError
                ? `${config.emptyText}. ${optionsError}`
                : config.emptyText}
            </EmptyStateText>
            <AddFirstOptionButton
              variant="secondary"
              size="small"
              onClick={onAddFirstOption}
              data-testid={`${props['data-testid']}-add-first-option-button`}
              noMinWidth
            >
              <StyledIconInButton
                className="uil uil-plus"
                size="medium"
                position="left"
              />
              {config.addFirstButtonText}
            </AddFirstOptionButton>
          </EmptyStateContainer>
        </>
      ) : (
        <>
          <AnswerOptionsLabel
            data-testid={`${props['data-testid']}-answer-options-title`}
          >
            {config.title}
            <RequiredMark>*</RequiredMark>
          </AnswerOptionsLabel>
          <OptionsListContainer
            data-testid={`${props['data-testid']}-options-list`}
          >
            {/* 'other' option temporarily commented: VAULT-18920 */}
            {visibleOptions.map((option, index) =>
              renderOption(option, index, index === visibleOptions.length - 1),
            )}
          </OptionsListContainer>
          {isAtMaxOptions && (
            <MaxOptionsAlert
              data-testid={`${props['data-testid']}-max-options-warning`}
              role="alert"
            >
              <StyledIcon
                className="uil uil-exclamation-circle"
                size="24px"
                color="#921A1D"
              />
              <MaxOptionsAlertMessage>
                {isDataGrid
                  ? 'Column limit reached. You cannot add more columns to this question.'
                  : 'Option limit reached. You cannot add more answers to this question.'}
              </MaxOptionsAlertMessage>
            </MaxOptionsAlert>
          )}
          {showDataGridFlag && (
            <DataGridFlagIfAnsweredRow>
              <Checkbox
                checked={flagIfAnswered}
                onChange={onFlagIfAnsweredChange}
                label="Flag if any row is added"
                labelPosition="right"
                clickableRow
                $fullWidthLabel
                data-testid={`${props['data-testid']}-flag-if-any-row-added`}
              />
            </DataGridFlagIfAnsweredRow>
          )}
          <AddOptionButton
            variant="secondary"
            size="small"
            onClick={onAddOption}
            data-testid={`${props['data-testid']}-add-option-button`}
            noMinWidth
            disabled={isAtMaxOptions}
          >
            <StyledIconInButton
              className="uil uil-plus"
              size="medium"
              position="left"
            />
            {config.addButtonText}
          </AddOptionButton>
        </>
      )}
    </AnswerOptionsContainer>
  );
};
